<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="btt"
    size="98%"
    :append-to-body="true"
    :destroy-on-close="true"
    :with-header="true"
    :close-on-press-escape="false"
    :modal="true"
    :before-close="handleClose"
    custom-class="compose-email-drawer"
    :show-close="mode == 'compose'"
    :withHeader="mode == 'compose'"
  >
    <div slot="title" class="title">
      {{ drawerTitle }}
    </div>
    <div class="compose-box">
      <div class="compose-wrap">
        <el-form
          ref="form"
          :model="form"
          class="compose-form"
          label-width="80px"
        >
          <el-form-item label="收件人">
            <el-input
              v-model="form.to"
              placeholder="请输入收件人，多个以分号分隔"
              size="small"
            />
          </el-form-item>
          <el-form-item label="抄送">
            <el-input
              v-model="form.cc"
              placeholder="请输入抄送人，多个以分号分隔"
              size="small"
            />
          </el-form-item>
          <el-form-item label="主题">
            <el-input
              v-model="form.subject"
              placeholder="请输入主题"
              size="small"
            />
          </el-form-item>

          <!-- 附件区域 -->
          <el-form-item label="附件">
            <div class="attachment-bar">
              <el-button
                size="mini"
                icon="el-icon-upload2"
                @click="triggerFileSelect"
              >上传附件</el-button
              >
              <input
                ref="fileInput"
                type="file"
                multiple
                style="display: none"
                @change="handleFileSelect"
              />
            </div>
            <div v-if="form.attachments.length > 0" class="attachment-list">
              <div
                v-for="(file, idx) in form.attachments"
                :key="idx"
                class="attachment-tag"
              >
                <i class="el-icon-document" />
                <span class="file-name" :title="file.name">{{
                  file.name
                }}</span>
                <i
                  class="el-icon-close remove-icon"
                  @click="removeAttachment(idx)"
                />
              </div>
            </div>
          </el-form-item>

          <el-form-item label="正文" class="editor-item">
            <vue-editor v-model="form.content" class="email-editor" />
          </el-form-item>
        </el-form>

        <!-- 回复/转发时展示原邮件 -->
        <div v-if="mode !== 'compose' && replyEmail" class="original-email">
          <div class="original-title">
            {{ mode == "forward" ? "转发" : ""
            }}{{ mode == "reply" ? "回复原" : "" }}邮件
          </div>
          <div class="original-box">
            <div class="original-header">
              <div class="original-row">
                <span class="original-label">主题：</span>
                <span class="original-value">{{ replyEmail.subject }}</span>
              </div>
              <div class="original-row">
                <span class="original-label">发件人：</span>
                <span class="original-value">{{ replyEmail.senderName }}</span>
              </div>
              <div class="original-row">
                <span class="original-label">收件人：</span>
                <span class="original-value">{{ replyEmail.recipients }}</span>
              </div>
              <div v-if="replyEmail.cc" class="original-row">
                <span class="original-label">抄送：</span>
                <span class="original-value">{{ replyEmail.cc }}</span>
              </div>
              <div class="original-row">
                <span class="original-label">发送时间：</span>
                <span class="original-value">{{
                  replyEmail.receivedDate
                }}</span>
              </div>
              <div
                v-if="replyEmail.attachments && replyEmail.attachments.length"
                class="original-row"
              >
                <span class="original-label">附件：</span>
                <div class="original-attachments">
                  <span
                    v-for="(att, i) in replyEmail.attachments"
                    :key="i"
                    class="original-att-item"
                  >
                    <i class="el-icon-document" /> {{ att.fileName }}
                  </span>
                </div>
              </div>
            </div>
            <div class="original-body" v-html="replyEmail.content" />
          </div>
        </div>
      </div>
      <div class="drawer-footer">
        <el-button size="small" @click="handleClose">取消</el-button>
        <el-button
          v-if="this.mode != 'draft'"
          size="small"
          type="info"
          plain
          class="draft"
          @click="handleSend(true)"
        >存草稿</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="loading"
          class="send"
          @click="handleSend"
        >发送</el-button
        >
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { sendEmailWithFiles, draftEmailWithFiles } from '@/api/email'

export default {
  name: 'ComposeEmailDrawer',
  components: { VueEditor },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'compose' // compose | reply | forward | draft
    },
    email: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      innerVisible: this.visible,
      form: {
        to: '',
        cc: '',
        subject: '',
        content: '',
        attachments: []
      },
      replyEmail: null,
      loading: false
    }
  },
  computed: {
    drawerTitle() {
      const map = { compose: '写邮件', reply: '回复邮件', forward: '转发邮件' }
      return map[this.mode] || '写邮件'
    }
  },
  watch: {
    visible(val) {
      this.innerVisible = val
      if (val) {
        this.initForm()
      }
    }
  },
  methods: {
    initForm() {
      const email = this.email
      if (this.mode === 'reply' && email) {
        this.form.to = email.sender ? email.sender.address || '' : ''
        this.form.cc = ''
        this.form.subject = email.subject
          ? email.subject.startsWith('Re:')
            ? email.subject
            : 'Re: ' + email.subject
          : ''
        this.form.content = '<p><br/></p><p><br/></p>'
        this.replyEmail = this.buildReplyEmail(email)
      } else if (this.mode === 'forward' && email) {
        this.form.to = ''
        this.form.cc = ''
        this.form.subject = email.subject
          ? email.subject.startsWith('Fw:')
            ? email.subject
            : 'Fw: ' + email.subject
          : ''
        this.form.content = '<p><br/></p><p><br/></p>'
        this.replyEmail = this.buildReplyEmail(email)
      } else if (this.mode === 'draft' && email) {
        this.form.subject = email.subject
        this.form.content = email.content
        this.form.to = email.recipients
          ? email.recipients.map((x) => x.address).join(';')
          : ''
        this.form.cc = email.cc ? email.cc.map((x) => x.address).join(';') : ''
        this.replyEmail = null
      } else {
        this.form = {
          to: '',
          cc: '',
          subject: '',
          content: '',
          attachments: []
        }
        this.replyEmail = null
      }
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    buildReplyEmail(email) {
      return {
        subject: email.subject || '',
        senderName: email.sender
          ? email.sender.name || email.sender.address
          : '',
        recipients: email.recipients
          ? email.recipients.map((r) => r.address).join('、')
          : '',
        cc: email.cc ? email.cc.map((c) => c.address).join('、') : '',
        receivedDate: email.receivedDate || '',
        attachments: email.attachments || [],
        content: email.content || ''
      }
    },
    triggerFileSelect() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(e) {
      const files = Array.from(e.target.files)
      files.forEach((file) => {
        this.form.attachments.push({
          name: file.name,
          size: file.size,
          rawFile: file,
          status: 'ready'
        })
      })
      // 清空input以便重复选择相同文件
      this.$refs.fileInput.value = ''
    },
    removeAttachment(idx) {
      this.form.attachments.splice(idx, 1)
    },
    handleClose() {
      this.innerVisible = false
      this.$emit('close')
    },
    async handleSend(isDraft = false) {
      if (!this.form.to.trim()) {
        this.$message.warning('请填写收件人')
        return
      }
      if (!this.form.subject.trim()) {
        this.$message.warning('请填写主题')
        return
      }
      if (!this.form.content.trim()) {
        this.$message.warning('请填写内容')
        return
      }

      this.loading = true
      try {
        const formData = new FormData()
        formData.append('to', this.form.to)
        formData.append('cc', this.form.cc || '')
        formData.append('subject', this.form.subject)
        if (this.mode === 'draft') {
          formData.append('draftEmailId', this.email.id)
        }
        formData.append('body', this.form.content || '')

        let sendType = '0'
        if (this.mode === 'reply') sendType = '1'
        if (this.mode === 'forward') sendType = '2'
        formData.append('sendType', sendType)

        if (this.mode !== 'compose' && this.email && this.email.messageId) {
          formData.append('messageId', this.email.messageId)
        }

        if (this.form.attachments && this.form.attachments.length > 0) {
          this.form.attachments.forEach((file) => {
            formData.append('files', file.rawFile)
          })
        }

        if (isDraft === true) {
          const res = await draftEmailWithFiles(formData)
          if (res.data.success) {
            this.$message.success('暂存成功')
            this.$emit('close')
          } else {
            this.$message.error(res.data.errorMessage || '暂存失败')
          }
        } else {
          const res = await sendEmailWithFiles(formData)
          if (res.data.success) {
            this.$message.success('发送成功')
            this.$emit('sent')
            this.$emit('close')
          } else {
            this.$message.error(res.data.errorMessage || '发送失败')
          }
        }
      } catch (err) {
        console.error(err)
        this.$message.error('发送异常')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.title {
  font-size: 15px;
  font-weight: bold;
}
.compose-box {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.compose-wrap {
  padding: 10px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.compose-form {
  flex: 1;
  /deep/ .el-form-item__label {
    padding-bottom: 4px;
    font-size: 12px;
    color: #666;
  }
  /deep/ .el-form-item {
    margin-bottom: 12px;
  }
}
.editor-item {
  /deep/ .el-form-item__content {
    line-height: 1;
  }
}
.email-editor {
  /deep/ .quill-editor {
    .ql-container {
      min-height: 150px;
      max-height: 300px;
      overflow-y: auto;
    }
  }
}
.attachment-bar {
  margin-bottom: 8px;
}
.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.attachment-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0px 8px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  max-width: 200px;
  .file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
  .remove-icon {
    cursor: pointer;
    font-size: 12px;
    color: #c0c4cc;
    &:hover {
      color: #f56c6c;
    }
  }
}

.original-email {
  margin-top: 16px;
  padding-top: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
}
.original-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}
.original-header {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 12px;
}
.original-box {
  font-size: 12px;
  color: #666;
  flex: 1;
}
.original-row {
  margin-bottom: 6px;
  display: flex;
  align-items: flex-start;
  &:last-child {
    margin-bottom: 0;
  }
}
.original-label {
  color: #999;
  flex-shrink: 0;
  margin-right: 4px;
}
.original-value {
  color: #666;
  word-break: break-all;
}
.original-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.original-att-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 3px;
  font-size: 11px;
}
.original-body {
  margin-top: 10px;
  padding-top: 10px;
  line-height: 1.6;
  /deep/ p {
    margin: 0 0 6px 0;
  }
}
.drawer-footer {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  > button {
    width: 90px;
  }
  .send {
    background: #5753e8;
    border-color: #5753e8;
    color: white;
  }
  .draft {
    background: #35ba83;
    border-color: #35ba83;
    color: white;
  }
}
</style>

<style lang="less">
.compose-email-drawer {
  border-radius: 6px 6px 0 0;
}
</style>
